import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { InfoButton } from './InfoModal';
import { compareSsCode } from '../utils/ssCodeSort';

const FOCUS_TITLE = {
  confirm: 'Confirm',
  repair: 'Repair',
  defect_send: 'Defect send',
  issued_today: 'Issued today',
  bwof_ready: 'Ready to submit',
};

const DESK_FOCUS = new Set(['', 'confirm', 'repair', 'defect_send']);

/** Open company-board rows: confirm, repair, or defect send. Nearest due first. */
export function boardOpenRows(board) {
  const out = [];
  for (const building of board?.buildings || []) {
    for (const row of building.rows || []) {
      const cells = row.cells || {};
      const keys = ['1', '2', '3', '4', '5'].filter((key) => cells[key]);
      if (!keys.length) continue;
      out.push({
        key: `${building.building_id}|${row.ss_label}|${row.due || ''}`,
        building_id: building.building_id,
        building_name: building.building_name,
        look_path: building.look_path || `/buildings/${building.building_id}/paperwork`,
        ss_label: row.ss_label || '',
        due: row.due || '',
        due_label: row.due_label || '',
        keys,
        action: keys.map((key) => cells[key].text).join(' · '),
      });
    }
  }
  out.sort((a, b) => {
    const due = String(a.due || '9999').localeCompare(String(b.due || '9999'));
    if (due) return due;
    return String(a.building_name || '').localeCompare(String(b.building_name || ''));
  });
  return out;
}

function deskRowsForFocus(rows, focus) {
  if (focus === 'confirm') return rows.filter((row) => row.keys.includes('1'));
  if (focus === 'repair') {
    return rows.filter((row) => row.keys.some((key) => key === '2' || key === '3' || key === '5'));
  }
  if (focus === 'defect_send') return rows.filter((row) => row.keys.includes('4'));
  return rows.slice(0, 5);
}

function formatDate(v) {
  if (!v) return '—';
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function typeBadge(type) {
  if (type === 'FORM_12A') return 'bg-emerald-100 text-emerald-800';
  if (type === 'S_RAD') return 'bg-amber-100 text-amber-900';
  if (type === 'DEFECT_NOTICE') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-700';
}

export default function PaperworkQueuePanel({ data, boardRows = [], focus = '', onFocusChange }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const issuedToday = useMemo(
    () => [...(data?.issued_today || [])].sort((a, b) => compareSsCode(a?.ss_code, b?.ss_code)),
    [data?.issued_today]
  );
  const defects = useMemo(
    () => [...(data?.defects || [])].sort((a, b) => compareSsCode(a?.ss_code, b?.ss_code)),
    [data?.defects]
  );
  const bwofReady = data?.bwof_ready || [];

  const activeList = useMemo(() => {
    if (focus === 'issued_today') return issuedToday;
    if (focus === 'defect') return defects;
    if (focus === 'bwof_ready') return bwofReady;
    return [];
  }, [focus, issuedToday, defects, bwofReady]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeList;
    return activeList.filter((row) => {
      const bits = [
        row.building_name,
        row.building_address,
        row.ss_code,
        row.ss_name,
        row.paperwork_label,
        row.iqp_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return bits.includes(q);
    });
  }, [activeList, search]);

  const isDesk = DESK_FOCUS.has(focus);
  const deskRows = useMemo(() => deskRowsForFocus(boardRows, focus), [boardRows, focus]);
  const isBuildingList = focus === 'bwof_ready';

  return (
    <div className="bg-card rounded-lg shadow mb-8 overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-xl font-semibold text-gray-900">
            {focus ? FOCUS_TITLE[focus] : 'Nearest due'}
          </h2>
          <InfoButton title="Paperwork">
            <div className="space-y-2 text-sm">
              <p>
                <strong>Confirm</strong> — inspection finished, Form 12A or S-RaD not issued yet (Full).
              </p>
              <p>
                <strong>Repair</strong> — repair or reinspection still open (Full, Partly, or External).
              </p>
              <p>
                <strong>Defect send</strong> — a defect notice has not been emailed yet (Partly).
              </p>
              <p>
                With no card selected, this list is the five open items with the nearest due date.
              </p>
              <p>
                <strong>Ready to submit</strong> — buildings where every active SS has Form 12A or S-RaD.
                Full agent: before council lodgement. Partial: before send to Main agent.
              </p>
            </div>
          </InfoButton>
        </div>
        {!focus ? (
          <p className="text-sm text-gray-500 mt-2">
            Five open items with the nearest due date. Tap a card to see that group.
          </p>
        ) : null}
      </div>

      {isDesk ? (
        <DeskList
          rows={deskRows}
          search={search}
          setSearch={setSearch}
          onClear={focus ? () => onFocusChange?.('') : null}
        />
      ) : focus ? (
        <>
          <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search building or SS…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => onFocusChange?.('')}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Nearest due
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm mt-2">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-6 py-3">Building</th>
                  {!isBuildingList ? <th className="px-4 py-3">SS</th> : <th className="px-4 py-3">SS count</th>}
                  {!isBuildingList ? <th className="px-4 py-3">Paperwork</th> : null}
                  <th className="px-4 py-3">{isBuildingList ? 'Latest issued' : 'Issued'}</th>
                  {!isBuildingList ? <th className="px-4 py-3">IQP</th> : null}
                  <th className="px-4 py-3">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((row) => (
                  <tr
                    key={row.key || row.building_id}
                    className="hover:bg-blue-50/40 cursor-pointer"
                    onClick={() => {
                      const tab = isBuildingList ? 'council' : 'forms';
                      navigate(`/buildings/${row.building_id}/paperwork?tab=${tab}`);
                    }}
                  >
                    <td className="px-6 py-3">
                      <p className="font-medium text-gray-900">{row.building_name}</p>
                      {row.building_address ? (
                        <p className="text-xs text-gray-500 truncate max-w-xs">{row.building_address}</p>
                      ) : null}
                    </td>
                    {!isBuildingList ? (
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{row.ss_code}</p>
                        {row.ss_name && row.ss_name !== row.ss_code ? (
                          <p className="text-xs text-gray-500">{row.ss_name}</p>
                        ) : null}
                      </td>
                    ) : (
                      <td className="px-4 py-3 tabular-nums">{row.ss_count}</td>
                    )}
                    {!isBuildingList ? (
                      <td className="px-4 py-3">
                        {row.paperwork_type ? (
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${typeBadge(row.paperwork_type)}`}
                          >
                            {row.paperwork_label || row.paperwork_type}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-gray-700 tabular-nums">
                      {formatDate(row.latest_issued_date || row.issued_date || row.issued_at)}
                    </td>
                    {!isBuildingList ? (
                      <td className="px-4 py-3 text-gray-700">{row.iqp_name || '—'}</td>
                    ) : null}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-primary-700 hover:underline text-xs font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          const tab = isBuildingList ? 'council' : 'forms';
                          navigate(`/buildings/${row.building_id}/paperwork?tab=${tab}`);
                        }}
                      >
                        Paperwork
                      </button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={isBuildingList ? 4 : 6} className="px-6 py-10 text-center text-gray-500">
                      {focus === 'issued_today' && 'No paperwork issued today.'}
                      {focus === 'defect' && 'No open defect notices.'}
                      {focus === 'bwof_ready' &&
                        'No buildings with every SS on Form 12A or S-RaD yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function DeskList({ rows, search, setSearch, onClear }) {
  const navigate = useNavigate();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.building_name, row.ss_label, row.action, row.due_label]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  return (
    <>
      <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search building or SS…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Nearest due
          </button>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm mt-2">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-6 py-3">Building</th>
              <th className="px-4 py-3">SS</th>
              <th className="px-4 py-3">To do</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((row) => (
              <tr
                key={row.key}
                className="hover:bg-blue-50/40 cursor-pointer"
                onClick={() => navigate(row.look_path)}
              >
                <td className="px-6 py-3 font-medium text-gray-900">{row.building_name}</td>
                <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{row.ss_label}</td>
                <td className="px-4 py-3 text-gray-800">{row.action}</td>
                <td className="px-4 py-3 text-gray-700 tabular-nums whitespace-nowrap">{row.due_label || '—'}</td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    className="text-primary-700 hover:underline text-xs font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(row.look_path);
                    }}
                  >
                    Paperwork
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                  No open confirm, repair, or defect send.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export const PAPERWORK_STAT_CARDS = [
  {
    id: 'confirm',
    name: 'Confirm',
    unit: 'SS',
    hint: 'Inspected, no Form 12A or S-RaD',
    icon: FileCheck,
    color: 'bg-slate-700',
  },
  {
    id: 'repair',
    name: 'Repair',
    unit: 'SS',
    hint: 'Repair or reinspection still open',
    icon: AlertTriangle,
    color: 'bg-orange-600',
  },
  {
    id: 'defect_send',
    name: 'Defect send',
    unit: 'SS',
    hint: 'Defect notice not emailed yet',
    icon: AlertTriangle,
    color: 'bg-amber-600',
  },
  {
    id: 'bwof_ready',
    name: 'Ready to submit',
    unit: 'buildings',
    hint: 'All SS Form 12A or S-RaD',
    icon: CheckCircle2,
    color: 'bg-emerald-600',
  },
];
